import { Container, Button, Box, Typography, CircularProgress, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import { UpdateRequestBody } from '@inversor-hub/types';
import {
  useDeleteInvestmentMainImage,
  useGetInvestment,
  useUpdateInvestment,
  useUploadInvestmentMainImage,
} from '@/lib/api/investments.ts';
import { GoogleMapsProvider } from '@/context/GoogleMapsProvider.tsx';
import InvestmentForm from '@/features/investments/form/components/InvestmentForm.tsx';
import UpgradeWallModal from '@/features/billing/components/UpgradeWallModal.tsx';
import { useInvestmentForm } from '@/features/investments/form/useInvestmentForm.ts';
import useSnackbar from '@/hooks/useSnackbar.tsx';
import { useInvestorInvestmentAccess } from '@/features/billing/useInvestorInvestmentAccess.ts';
import { useListInvestmentRentalPeriods } from '@/lib/api/investment-rental-periods.ts';

const UpdateInvestmentPage = () => {
  const navigate = useNavigate();
  const { investmentId } = useParams<{ investmentId: string }>();
  const updateInvestment = useUpdateInvestment();
  const uploadMainImage = useUploadInvestmentMainImage();
  const deleteMainImage = useDeleteInvestmentMainImage();
  const {
    isLockedInvestment,
    isLoading: isLoadingAccess,
    upgradeWallInvestmentCount,
    upgradeWallPlansPath,
  } = useInvestorInvestmentAccess();
  const { show: showSnackbar } = useSnackbar();

  const {
    data: investment,
    isLoading: isLoadingInvestment,
    error: loadError,
  } = useGetInvestment(investmentId);
  const {
    data: rentalPeriods,
    isLoading: isLoadingRentalPeriods,
    isError: isRentalPeriodsError,
  } = useListInvestmentRentalPeriods(investmentId);

  const { initialFormData, handleSubmit, handleErrorDismiss, error, isLoading } = useInvestmentForm(
    {
      initialData: investment,
      hasRentalHistory: Boolean(rentalPeriods?.length),
      onSubmit: async ({ payload, mainImageFile, removeExistingMainImage }) => {
        if (!investmentId) return;

        await updateInvestment.mutateAsync({
          investmentId,
          data: payload as UpdateRequestBody,
        });

        if (mainImageFile) {
          await uploadMainImage.mutateAsync({
            investmentId,
            file: mainImageFile,
          });
        } else if (removeExistingMainImage) {
          await deleteMainImage.mutateAsync(investmentId);
        }
      },
      onSuccess: () => {
        navigate(`/inversiones/${investmentId}`);
        showSnackbar({ title: 'Inversión actualizada', variant: 'success' });
      },
    }
  );

  const handleCancel = () => {
    navigate(`/inversiones/${investmentId}`);
  };

  if (isLoadingInvestment || isLoadingAccess || isLoadingRentalPeriods) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (isLockedInvestment(investmentId)) {
    return (
      <UpgradeWallModal
        open
        onClose={() => navigate('/inversiones')}
        onViewPlans={() => navigate(upgradeWallPlansPath)}
        investmentCount={upgradeWallInvestmentCount}
      />
    );
  }

  if (loadError || !investment) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button startIcon={<ArrowBack />} onClick={handleCancel} sx={{ mb: 2 }}>
            Volver
          </Button>
        </Box>
        <Alert severity="error">Error al cargar la inversión. Por favor, inténtalo de nuevo.</Alert>
      </Container>
    );
  }

  if (isRentalPeriodsError) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button startIcon={<ArrowBack />} onClick={handleCancel} sx={{ mb: 2 }}>
            Volver
          </Button>
        </Box>
        <Alert severity="error">
          Error al cargar el historial de alquileres. Por favor, inténtalo de nuevo.
        </Alert>
      </Container>
    );
  }

  return (
    <GoogleMapsProvider>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button startIcon={<ArrowBack />} onClick={handleCancel} sx={{ mb: 2 }}>
            Volver
          </Button>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Editar inversión
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Actualiza la información de tu inversión inmobiliaria
          </Typography>
        </Box>

        <InvestmentForm
          initialData={initialFormData}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          submitLabel="Guardar cambios"
          loadingLabel="Guardando..."
          isLoading={isLoading}
          error={error}
          hasRentalHistory={Boolean(rentalPeriods?.length)}
          onErrorDismiss={handleErrorDismiss}
        />
      </Container>
    </GoogleMapsProvider>
  );
};

export default UpdateInvestmentPage;
